import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { X } from 'lucide-react';

const Gallery = ({ isDarkMode, toggleDarkMode }) => {
  const { category } = useParams();
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    const importImages = async () => {
      try {
        // eslint-disable-next-line
        const context = require.context('!file-loader!../../public/gallery', true, /\.(png|jpe?g|svg|mp4)$/);
        const imagePromises = [];
        const collections = {};

        const categoryFolder = category === '2d' ? '2D' : category === '3d' ? '3D' : 'Video';

        const keys = context.keys();
        keys.forEach((key, index) => {
          if (key.includes(`/${categoryFolder}/`)) {
            const pathParts = key.split('/');
            const parentFolder = pathParts[pathParts.length - 2];
            const gridMatch = parentFolder.match(/^(\d+)/);
            const gridColumns = gridMatch ? parseInt(gridMatch[1]) : null;

            const imageName = key.split('/').pop().replace(/\.[^/.]+$/, '');
            const fileExtension = key.split('.').pop().toLowerCase();
            const isVideo = fileExtension === 'mp4';

            const fullPath = key.replace('./', '');

            const imageItem = {
              id: index,
              title: imageName,
              image: `/gallery/${fullPath}`,
              medium: isVideo ? 'Video' : 'Artwork',
              year: '2024',
              isVideo: isVideo,
              parentFolder: parentFolder
            };

            if (gridColumns) {
              if (!collections[parentFolder]) {
                collections[parentFolder] = {
                  name: parentFolder,
                  gridColumns: gridColumns,
                  items: []
                };
              }

              if (!isVideo) {
                // For images, load image to get dimensions
                const imagePromise = new Promise((resolve, reject) => {
                  const img = new Image();
                  img.src = imageItem.image;
                  img.onload = () => {
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    collections[parentFolder].items.push({
                      ...imageItem,
                      aspectRatio
                    });
                    resolve();
                  };
                  img.onerror = (error) => {
                    console.error('Error loading image:', imageItem.image, error);
                    collections[parentFolder].items.push({
                      ...imageItem,
                      aspectRatio: 1
                    });
                    resolve();
                  };
                });
                imagePromises.push(imagePromise);
              } else {
                // For videos, set default aspectRatio
                collections[parentFolder].items.push({
                  ...imageItem,
                  aspectRatio: 16 / 9 // Adjust if needed based on your video aspect ratios
                });
              }
            } else {
              // Handle images not in collections if necessary
            }
          }
        });

        await Promise.all(imagePromises);

        const collectionsArray = Object.values(collections);

        // Sort collections by name or any other criteria if needed
        setGalleryItems(collectionsArray);
      } catch (error) {
        console.error('Error loading images:', error);
      }
    };

    if (category) {
      importImages();
    }
  }, [isDarkMode, category]);

  const getCategoryTitle = () => {
    switch (category) {
      case '2d':
        return '2D';
      case '3d':
        return '3D';
      case 'video':
        return 'Video';
      default:
        return 'All';
    }
  };

  const renderCollection = (collection) => {
    const useGrid = collection.gridColumns > 4;

    const containerStyle = useGrid ? {
      display: 'grid',
      gap: '0.5rem',
      gridTemplateColumns: `repeat(${collection.gridColumns}, minmax(0, 1fr))`
    } : {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'stretch',
      flexWrap: 'nowrap'
    };

    return (
      <div key={collection.name} className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 dark:text-white">{collection.name}</h2>
        <div style={containerStyle}>
          {collection.items.map((artwork) => (
            <div 
              key={artwork.id}
              className="relative group"
              onClick={() => !artwork.isVideo && setSelectedArtwork(artwork)}
              style={useGrid ? {} : { flexGrow: artwork.aspectRatio || 1, flexBasis: 0 }}
            >
              <div className="relative bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-lg transition-all duration-300 hover:shadow-xl h-full">
                {artwork.isVideo ? (
                  <div className="h-full bg-black">
                    <video 
                      className="w-full h-full object-contain"
                      controls
                    >
                      <source src={artwork.image} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <>
                    <div className="h-full">
                      <img 
                        src={artwork.image} 
                        alt={artwork.title} 
                        className="w-full h-full object-contain cursor-zoom-in"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/15 backdrop-blur-[2px] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="p-2 text-sm">{artwork.title}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGalleryItem = (artwork) => (
    <div 
      key={artwork.id}
      className="relative w-full"
      onClick={() => !artwork.isVideo && setSelectedArtwork(artwork)}
    >
      <div className="relative bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-lg group transition-all duration-300 hover:shadow-xl">
        {artwork.isVideo ? (
          <div className="h-full bg-black">
            <video 
              className="w-full h-full object-contain"
              controls
            >
              <source src={artwork.image} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ) : (
          <>
            <div className="h-full">
              <img 
                src={artwork.image} 
                alt={artwork.title} 
                className="w-full h-full object-contain cursor-zoom-in"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-black/15 backdrop-blur-[2px] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="p-2 text-sm">{artwork.title}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''} 
      bg-gradient-to-br from-neutral-50 via-blue-50 to-neutral-100 
      dark:bg-gradient-to-br dark:from-neutral-900 dark:via-blue-950 dark:to-neutral-900 
      text-neutral-900 dark:text-white transition-colors duration-500`}>
      <Navigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      <section className="pt-24 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center dark:text-white">{getCategoryTitle()}</h1>

          {galleryItems.length === 0 && (
            <p className="text-center text-xl text-neutral-600 dark:text-neutral-400">
              No images found in this category.
              <br />
              Please make sure your images are in the correct folder: /gallery/{category === '2d' ? '2D' : category === '3d' ? '3D' : 'Video'}
            </p>
          )}

          <div className="space-y-8">
            {galleryItems.map(item => (
              'gridColumns' in item ? renderCollection(item) : renderGalleryItem(item)
            ))}
          </div>
        </div>
      </section>

      {selectedArtwork && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArtwork(null)}
        >
          <div 
            className="relative max-w-7xl w-full bg-transparent overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 transition-colors"
              onClick={() => setSelectedArtwork(null)}
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <img
              src={selectedArtwork.image}
              alt={selectedArtwork.title}
              className="w-full h-auto max-h-[85vh] object-contain"
            />

            <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm text-white p-4">
              <h3 className="text-xl font-semibold">{selectedArtwork.title}</h3>
              <p className="text-neutral-300">{selectedArtwork.medium}, {selectedArtwork.year}</p>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-16 bg-gradient-to-br from-neutral-100 to-blue-50 dark:from-neutral-800 dark:to-blue-950 py-6 text-center transition-colors duration-500">
        <p className="text-neutral-600 dark:text-neutral-400">© 2024 EL Ceballos. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Gallery;